// ================================
// IMPORTS
// ================================
import { defineStore } from "pinia"
import { ref, computed, inject } from "vue"
import { jwtDecode } from "jwt-decode"
import { useFetchWrapper } from "@core/composables"
import type { Company } from "@types"
import type { CompanyPlafond, StripeSubscription } from "@core/types/auth"
import { lirePlafondServi } from "@core/composables/plafond-forfait.helper"
import {
    hasLiveSocleSubscription,
    hasLiveSubscriptionWithTag,
    resolveSubscriptionPlan,
} from "@core/helpers/subscription-plan.helper"
import type { Address } from "@types"
import { useTaskStore } from "./task.store"
import { useRootStore } from "./root.store"
import { Services } from "@services"
import {
    AUTH_TOKEN_KEY,
    ADMIN_HOMEYS_KEY,
    SESSION_ID_KEY,
} from "./auth.constants"

/**
 * Valeurs des tags métadonnées Stripe (`metadata.tag`) pour les abonnements.
 */
export const SUBSCRIPTIONS_TAGS = {
    PV: "pv",
    IRVE: "irve",
    CHAUFFAGE: "chauffage",
    OPTIM_TURPE: "optim-turpe",
    ENERGY_MANAGER: "energymanager",
    ENERGY_ANALYTICS: "energyanalytics",
    IOT: "iot",
    AUDIT_ENERGETIQUE: "be",
    COMPARATEUR_OFFRE: "comparateur_offre",
    OPTIM_TURPE_DASHBOARD: "optim-turpe-dashboard",
    ANALYSE_FACTURE: "analyse-facture",
} as const

/**
 * Store permettant de gérer l'authentification de l'utilisateur au widget
 */
export const useAuthStore = defineStore("auth", () => {
    // ================================
    // STORES UTILISÉS
    // ================================
    let taskStore: ReturnType<typeof useTaskStore>

    const { $getCurrentUser } = inject<Services>("services")!

    // ================================
    // ÉTAT AUTHENTIFICATION
    // ================================

    /**
     * Token JWT en mémoire pour l'authentification de l'utilisateur
     */
    const token = ref<string>("")

    /**
     * Route précédente avant la connexion pour redirection après authentification
     */
    const previousRoute = ref<string>("")

    /**
     * Utilisateur prérempli par l’app parente (embed) : le premier {@link fetchUser} saute l’appel GET /user/me.
     */
    const userFromEmbed = ref(false)

    /**
     * Tracking V0 — flag staff Homeys (master password détecté au login).
     * Drive `actor_type` ("homeys_staff" | "client") de l'enveloppe canonique.
     * Persisté en localStorage. Cleared au logout.
     */
    const adminHomeys = ref<boolean>(false)

    /**
     * Tracking V0 — identifiant unique de la session (UUID v4).
     * Généré au login, persiste pendant la session, cleared au logout.
     */
    const sessionId = ref<string>("")

    // ================================
    // DONNÉES UTILISATEUR
    // ================================

    /**
     * Identifiant unique de l'utilisateur connecté
     */
    const userId = ref<string>("")

    /**
     * Prénom de l'utilisateur connecté
     */
    const firstname = ref<string>("")

    /**
     * Nom de l'utilisateur connecté
     */
    const lastname = ref<string>("")

    /**
     * Numéro de téléphone de l'utilisateur connecté
     */
    const phone = ref<string | null>(null)

    /**
     * Paramètres d'alarme/utilisateur
     */
    const alarme = ref<any | null>(null)

    /**
     * Nom d'utilisateur (email) de l'utilisateur connecté
     */
    const username = ref<string>("")

    /**
     * URL de l'image de profil de l'utilisateur
     */
    const img = ref<string | null>(null)

    // ================================
    // DONNÉES ENTREPRISE
    // ================================

    /**
     * Identifiant de l'entreprise de l'utilisateur connecté
     */
    const companyId = ref<string>("")

    /**
     * Nom de l'entreprise (extrait au fetch)
     */
    const companyName = ref<string>("")

    /**
     * Identifiant SIREN/RNA etc. de l'entreprise (extrait au fetch)
     */
    const companyIdcompany = ref<string | undefined>(undefined)

    /**
     * Forme juridique de l'entreprise (extrait au fetch)
     */
    const companyLegalForm = ref<string | undefined>(undefined)

    /**
     * Adresse de l'entreprise (extrait au fetch)
     */
    const companyAddress = ref<Address | undefined>(undefined)

    /**
     * Type d'organisation entreprise, asso, copro, collectivite (extrait au fetch)
     */
    const companyType = ref<
        "entreprise" | "asso" | "copro" | "collectivite" | undefined
    >(undefined)

    /**
     * Représentant de l'organisation (extrait au fetch)
     */
    const companyRepresentant = ref<string | undefined>(undefined)

    /**
     * Date de création de l'organisation chez Homeys (extrait au fetch)
     */
    const companyCreatedAt = ref<string | Date | undefined>(undefined)

    /**
     * Téléphone de l'organisation (extrait au fetch)
     */
    const companyPhone = ref<string | undefined>(undefined)

    /**
     * Image de l'entreprise (extrait au fetch) : raw_image, crop_image, style_image
     */
    const companyImg = ref<
        | { raw_image?: string; crop_image?: string; style_image?: any }
        | undefined
    >(undefined)

    /**
     * Fonctionnalités beta activées pour l'entreprise (extrait au fetch)
     */
    const companyBetaFeatures = ref<Record<string, boolean>>({})

    /**
     * Liste des abonnements Stripe de l'entreprise (extrait au fetch)
     */
    const companyStripeSubscriptions = ref<StripeSubscription[]>([])

    /**
     * Nombre de sources actives (facturation) pour l'entreprise, mis à jour par les composants qui en ont besoin
     */
    const companySourcesCount = ref<number>(0)

    /**
     * Plafond du forfait servi par l'API (`company.operation.plafond`, DEV-2254).
     * `null` tant que l'API ne le sert pas : l'écran garde alors son calcul historique.
     */
    const companyPlafond = ref<CompanyPlafond | null>(null)

    /**
     * Remplace le plafond servi (réponse utilisateur, ou `GET /company/billing/source/count`
     * relu après chaque ajout réussi par `rafraichirPlafondServi`).
     * Un bloc absent ou mal formé remet `null` (repli sur le calcul historique).
     * @param plafond - Bloc `plafond` brut de la réponse
     */
    function setCompanyPlafond(plafond: unknown): void {
        companyPlafond.value = lirePlafondServi(plafond)
    }

    /**
     * Liste des entreprises multiples de l'utilisateur (si multi-company)
     */
    const multipleCompanies = ref<Company[]>([])

    /**
     * Rôle de l'utilisateur au sein de son entreprise (employe, employe_read_only, administrateur, owner, client).
     * `client` est l'utilisateur UC2 d'un espace (client d'un client Homeys) ; il n'a aucun droit
     * d'écriture, mais il a bien un rôle : le confondre avec `null` (« pas encore chargé ») cachait
     * des entrées de menu que les gardes de route lui ouvrent (DEV-2452).
     */
    const role = ref<
        | "employe"
        | "employe_read_only"
        | "administrateur"
        | "owner"
        | "client"
        | null
    >(null)

    /**
     * Informations sur l'abonnement de l'entreprise (plan et statut d'activité)
     */
    const subscription = ref<{
        plan: string
        active: boolean
    }>({
        plan: "",
        active: false,
    })

    // ================================
    // VALIDATION TOKEN
    // ================================

    /**
     * Vérifie si le token JWT est valide et non expiré
     * @returns True si le token est valide, false sinon
     */
    const getIsTokenValid = computed(() => {
        if (!token.value) return false
        try {
            const tokeDecode = jwtDecode(token.value) as {
                exp: number
            }
            // Vérifie si le token n'est pas expiré
            return tokeDecode.exp * 1000 > Date.now()
        } catch {
            return false
        }
    })

    /**
     * Indique si l'utilisateur est authentifié avec un token valide
     */
    const getIsLogged = computed(() => getIsTokenValid.value)

    // ================================
    // GESTION TOKEN
    // ================================

    /**
     * Supprime le token et les clés de tracking V0 du localStorage (côté client uniquement).
     */
    function clearTokenFromStorage() {
        if (typeof window !== "undefined") {
            try {
                window.localStorage.removeItem(AUTH_TOKEN_KEY)
                window.localStorage.removeItem(ADMIN_HOMEYS_KEY)
                window.localStorage.removeItem(SESSION_ID_KEY)
            } catch {
                // localStorage indisponible (ex. mode privé, quota)
            }
        }
    }

    // ================================
    // TRACKING V0 — adminHomeys + sessionId
    // ================================

    /**
     * Définit le flag `adminHomeys` (depuis `response.adminHomeys` du login)
     * et le persiste en localStorage. Cf. `front-v6/frontend/src/tracking/`.
     */
    function setAdminHomeys(value: boolean) {
        adminHomeys.value = value
        if (typeof window !== "undefined") {
            try {
                window.localStorage.setItem(
                    ADMIN_HOMEYS_KEY,
                    value ? "true" : "false"
                )
            } catch {
                // localStorage indisponible
            }
        }
    }

    /**
     * Génère un nouvel identifiant de session (UUID v4) et le persiste en localStorage.
     * Appelé une seule fois par session (au login). Au session restore, on lit la valeur existante.
     */
    function generateNewSession() {
        const uuid =
            typeof crypto !== "undefined" &&
            typeof crypto.randomUUID === "function"
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`
        sessionId.value = uuid
        if (typeof window !== "undefined") {
            try {
                window.localStorage.setItem(SESSION_ID_KEY, uuid)
            } catch {
                // localStorage indisponible
            }
        }
    }

    /**
     * Définit le token JWT dans le store et le persiste en localStorage (clé alignée legacy).
     * @param newToken - Nouveau token JWT à enregistrer
     */
    function setToken(newToken: string) {
        token.value = newToken
        if (typeof window !== "undefined") {
            try {
                if (newToken) {
                    window.localStorage.setItem(AUTH_TOKEN_KEY, newToken)
                } else {
                    clearTokenFromStorage()
                }
            } catch {
                // localStorage indisponible (ex. mode privé, quota)
            }
        }
    }

    /**
     * Ajoute le token d'authentification dans le header Authorization des requêtes HTTP
     * @param config - Configuration de la requête avec les headers
     */
    function setHeaderAuthorization(config: {
        headers: Record<string, string>
    }) {
        if (getIsLogged.value) {
            config.headers.authorization = `Bearer ${token.value}`
        }
    }

    // ================================
    // RÉCUPÉRATION DONNÉES UTILISATEUR
    // ================================

    /**
     * Remplit le store avec l'utilisateur fourni par l'app parente (ex. vcs + widget-loader en embed).
     * Permet d'éviter le premier GET /user/me lorsque le parent envoie déjà l'objet user via `postMessage`.
     * @param data - Objet user (forme API : `_id` ou `_id_user`, `company`, etc.)
     */
    function setUserFromEmbed(data: Record<string, unknown>) {
        const _id_user = (data._id_user as string) || (data._id as string) || ""
        userId.value = _id_user
        companyId.value = (data.company as { _id?: string })?._id || ""
        firstname.value = (data.firstname as string) || ""
        lastname.value = (data.lastname as string) || ""
        phone.value = (data.phone as string | null) ?? null
        const companyPayload = data.company as
            | Record<string, unknown>
            | undefined
        const userCompanyType = companyPayload?.userCompanyType as
            | string
            | undefined
        if (
            userCompanyType === "employe" ||
            userCompanyType === "employe_read_only" ||
            userCompanyType === "administrateur" ||
            userCompanyType === "owner" ||
            userCompanyType === "client"
        ) {
            role.value = userCompanyType
        } else {
            role.value = null
        }
        if (data.username) username.value = data.username as string
        if (data.img) img.value = data.img as string
        if (data.company) {
            const c = data.company as Record<string, unknown>
            const op = (c.operation as Record<string, unknown>) || {}
            const stripe = (op.stripe as Record<string, unknown>) || {}
            companyName.value = (c.name as string) || ""
            companyIdcompany.value = c.idcompany as string | undefined
            companyLegalForm.value = c.legalForm as string | undefined
            companyAddress.value = c.address as Address | undefined
            companyType.value = c.type as typeof companyType.value
            companyRepresentant.value = c.representant as string | undefined
            companyCreatedAt.value = c.createdAt as string | Date | undefined
            companyPhone.value = c.phone as string | undefined
            companyImg.value = c.img as typeof companyImg.value
            companyBetaFeatures.value =
                (op.beta_features as Record<string, boolean>) || {}
            companyStripeSubscriptions.value =
                (stripe.subscriptions as StripeSubscription[]) || []
            companySourcesCount.value =
                (op.count_active_source as number) ?? companySourcesCount.value
            companyPlafond.value = lirePlafondServi(op.plafond)
        } else {
            companyPlafond.value = null
        }
        if (data.multipleCompanies && Array.isArray(data.multipleCompanies)) {
            multipleCompanies.value = data.multipleCompanies as Company[]
        }
        if (data.alarme) alarme.value = data.alarme as typeof alarme.value
        const opStripe = companyPayload?.operation as
            | Record<string, unknown>
            | undefined
        const stripeEmbed =
            (opStripe?.stripe as {
                plan?: string
                subscription_active?: boolean
            }) || {}
        subscription.value = {
            plan: stripeEmbed.plan || "",
            active: stripeEmbed.subscription_active || false,
        }
        userFromEmbed.value = true
        taskStore = useTaskStore()
        taskStore.signInPusher(userId.value)
    }

    /**
     * Récupère les informations de l'utilisateur connecté depuis l'API et met à jour le store
     */
    const {
        fetch: fetchUserRaw,
        isFetching: isFetchingUser,
        hasError: hasErrorUser,
        error: errorUser,
        isSuccess: isSuccessUser,
    } = useFetchWrapper(async () => {
        if (userFromEmbed.value) {
            userFromEmbed.value = false
            return
        }
        const data = await $getCurrentUser()

        userId.value = data._id_user
        companyId.value = data.company?._id || ""
        firstname.value = data.firstname || ""
        lastname.value = data.lastname || ""
        phone.value = data.phone || null
        // phone n'est pas retourné par getCurrentUser, sera mis à jour via updateUserInfo

        // Affectation du rôle, en s'assurant que la valeur correspond au type Role ou null
        if (data?.company?.userCompanyType) {
            const type = data.company.userCompanyType as string
            if (
                type === "employe" ||
                type === "employe_read_only" ||
                type === "administrateur" ||
                type === "owner" ||
                type === "client"
            ) {
                role.value = type
            } else {
                role.value = null
            }
        } else {
            role.value = null
        }

        if (data.username) username.value = data.username

        if (data.img) img.value = data.img

        if (data.company) {
            const c = data.company as Record<string, unknown>
            const op = (c.operation as Record<string, unknown>) || {}
            const stripe = (op.stripe as Record<string, unknown>) || {}
            companyName.value = (c.name as string) || ""
            companyIdcompany.value = c.idcompany as string | undefined
            companyLegalForm.value = c.legalForm as string | undefined
            companyAddress.value = c.address as Address | undefined
            companyType.value = c.type as typeof companyType.value
            companyRepresentant.value = c.representant as string | undefined
            companyCreatedAt.value = c.createdAt as string | Date | undefined
            companyPhone.value = c.phone as string | undefined
            companyImg.value = c.img as typeof companyImg.value
            companyBetaFeatures.value =
                (op.beta_features as Record<string, boolean>) || {}
            companyStripeSubscriptions.value =
                (stripe.subscriptions as StripeSubscription[]) || []
            companySourcesCount.value =
                (op.count_active_source as number) ?? companySourcesCount.value
            companyPlafond.value = lirePlafondServi(op.plafond)
        } else {
            companyPlafond.value = null
        }

        // Entreprises multiples (si présentes)
        if (data?.multipleCompanies && Array.isArray(data.multipleCompanies)) {
            multipleCompanies.value = data.multipleCompanies
        }

        // Alarme notifications (si présentes)
        if (data?.alarme) alarme.value = data.alarme

        subscription.value = {
            plan:
                (data.company as { operation?: { stripe?: { plan?: string } } })
                    ?.operation?.stripe?.plan || "",
            active:
                (
                    data.company as {
                        operation?: {
                            stripe?: { subscription_active?: boolean }
                        }
                    }
                )?.operation?.stripe?.subscription_active || false,
        }

        // Initialise le store des tâches une fois l'utilisateur récupéré
        taskStore = useTaskStore()
        // authentifie pusher
        taskStore.signInPusher(userId.value)
    })

    /**
     * Promesse du fetch user en cours (déduplication des appels concurrents).
     */
    let fetchUserPromise: Promise<void> | null = null

    /**
     * Récupère les informations de l'utilisateur connecté (déduplique les appels simultanés).
     * Si un fetch est déjà en cours, retourne la même promesse.
     */
    async function fetchUser(): Promise<void> {
        if (fetchUserPromise) {
            return fetchUserPromise
        }
        fetchUserPromise = fetchUserRaw().finally(() => {
            fetchUserPromise = null
        })
        return fetchUserPromise
    }

    // ================================
    // STATUT ABONNEMENT
    // ================================

    /**
     * Plan de l'entreprise ("EM", "EA" ou "autre") DÉRIVÉ de la liste brute des
     * abonnements Stripe, via la règle miroir de la SSOT backend.
     *
     * Volontairement indépendant du champ dénormalisé `subscription.value.plan`
     * servi par l'API, qui peut rester figé indéfiniment (cf. DEV-1327 et la
     * documentation de `resolveSubscriptionPlan`).
     * @returns Le plan effectif de l'entreprise
     */
    const getSubscriptionPlan = computed(() =>
        resolveSubscriptionPlan(companyStripeSubscriptions.value)
    )

    /**
     * Vérifie si l'abonnement de l'entreprise est actif (plan payant Energy Manager ou Energy Audit)
     * @returns True si l'abonnement est actif, false sinon
     */
    const getIsSubscriptionActive = computed(() => {
        return getSubscriptionPlan.value !== "autre"
    })

    // ================================
    // TYPE CLIENT
    // ================================

    /**
     * Indique si l'utilisateur est un client direct Homeys avec un abonnement
     * @returns True si client Homeys avec abonnement, false sinon
     */
    const getIsClientHomeys = computed((): boolean => {
        return !!companyId.value && !!subscription.value?.plan
    })

    /**
     * Indique si l'utilisateur est un UC2 professionnel (client d'un client Homeys, sans abonnement direct)
     * @returns True si UC2 professionnel, false sinon
     */
    const getIsUc2Pro = computed((): boolean => {
        return !!companyId.value && !subscription.value?.plan
    })

    /**
     * Indique si l'utilisateur est un particulier (UC2 sans entreprise rattachée)
     * @returns True si particulier, false sinon
     */
    const getIsUc2Particulier = computed((): boolean => {
        return !companyId.value
    })

    /**
     * Indique si l'entreprise n'est plus cliente : aucune ligne d'offre socle
     * vivante (`active`, `past_due`, `trialing`). Une ligne `canceled` ou
     * `incomplete_expired` ne donne aucun droit (DEV-2252, cas ENEIDE : une
     * seule ligne jamais payée passait la porte). Tableau vide : non cliente.
     */
    const getIsClientCancelled = computed((): boolean => {
        return !hasLiveSocleSubscription(companyStripeSubscriptions.value)
    })

    /**
     * Indique si au moins un abonnement Stripe est en `past_due` (facture impayée).
     * Port à l'identique du getter `isUnpaidSubscriptions` v5 — déclenche le bandeau global.
     */
    const getHasUnpaidSubscriptions = computed((): boolean => {
        const subs = companyStripeSubscriptions.value ?? []
        return subs.some((s) => s.status === "past_due")
    })

    /**
     * Nombre de jours restants avant la suppression automatique des données de l'entreprise
     * après annulation d'abonnement (CGV art. 9.1 — fenêtre de 15 jours).
     *
     * Calculé depuis la date de fin (`ended_at`) du dernier abonnement annulé + 15j.
     * Renvoie `null` si la donnée n'est pas disponible (sub sans `ended_at`).
     * Renvoie un nombre négatif/zéro si la fenêtre est dépassée — le composant appelant
     * masque alors le badge pour ne pas dramatiser.
     */
    const getDaysUntilDataDeletion = computed((): number | null => {
        const subs = companyStripeSubscriptions.value ?? []
        const endedDates = subs
            .filter((s) => s.status === "canceled" && s.ended_at)
            .map((s) => new Date(s.ended_at as string).getTime())
            .filter((t) => Number.isFinite(t))
        if (endedDates.length === 0) return null
        const lastEnd = Math.max(...endedDates)
        const deletionDate = lastEnd + 15 * 24 * 60 * 60 * 1000
        return Math.ceil((deletionDate - Date.now()) / (24 * 60 * 60 * 1000))
    })

    // ================================
    // PERMISSIONS UTILISATEUR
    // ================================

    /**
     * Vérifie si l'utilisateur a des droits d'écriture ou supérieurs (employé, administrateur, owner)
     * @returns True si l'utilisateur a les permissions nécessaires, false sinon
     */
    const getIsEmployeWriteOrHigher = computed((): boolean => {
        return ["employe", "administrateur", "owner"].includes(role.value ?? "")
    })

    /**
     * Vérifie si l'utilisateur est administrateur ou propriétaire de l'entreprise
     * @returns True si administrateur ou owner, false sinon
     */
    const getIsCompanyAdmin = computed((): boolean => {
        return ["administrateur", "owner"].includes(role.value ?? "")
    })

    /**
     * Vérifie si l'utilisateur est propriétaire de l'entreprise
     * @returns True si propriétaire, false sinon
     */
    const getIsCompanyOwner = computed((): boolean => {
        return ["owner"].includes(role.value ?? "")
    })

    /**
     * Vérifie si l'utilisateur a l'offre Energy Manager (plan EM)
     * @returns True si l'utilisateur a l'offre Energy Manager, false sinon
     */
    const getHasOfferEnergyManager = computed((): boolean => {
        return ["EM"].includes(subscription.value?.plan ?? "")
    })

    /**
     * Récupère l'offre courante (Management ou Analytics) depuis les abonnements Stripe de l'entreprise.
     * Utilisé pour la période de facturation et la facturation des sources.
     * @returns L'offre courante (avec current_start_period_monthly, current_end_period_monthly, etc.) ou null
     */
    const getCurrentOffer = computed(() => {
        const subscriptions = companyStripeSubscriptions.value
        if (!subscriptions?.length) return null

        const offers = subscriptions.filter(
            (subscription: { name?: string }) =>
                subscription.name?.includes("Management") ||
                subscription.name?.includes("Analytics")
        )

        if (!offers?.length) return null

        const statusPriority: Record<string, number> = {
            active: 1,
            trialing: 3,
            past_due: 2,
            canceled: 4,
        }

        return offers.sort(
            (
                a: { status?: string; date_start?: string },
                b: { status?: string; date_start?: string }
            ) => {
                const aPriority = statusPriority[a.status ?? ""] ?? 999
                const bPriority = statusPriority[b.status ?? ""] ?? 999
                if (aPriority < bPriority) return -1
                if (aPriority > bPriority) return 1
                return (
                    new Date(b.date_start ?? 0).getTime() -
                    new Date(a.date_start ?? 0).getTime()
                )
            }
        )[0]
    })

    // ================================
    // FONCTIONS UTILITAIRES
    // ================================

    /**
     * Vérifie si l'utilisateur a une souscription active avec un tag spécifique
     * @param tag - Le tag à rechercher dans les métadonnées des souscriptions
     * @returns True si une souscription active contient le tag, false sinon
     */
    function hasSubscriptionWithTag(tag: string): boolean {
        // ligne vivante seulement : ni résiliée ni jamais payée (DEV-2252)
        return hasLiveSubscriptionWithTag(companyStripeSubscriptions.value, tag)
    }

    // ================================
    // MODULES & ADDONS
    // ================================

    /**
     * Vérifie si l'utilisateur a accès au module Optimisation Contractuelle
     * @returns True si l'utilisateur a accès au module, false sinon
     */
    const getHasModuleOptimTurpe = computed((): boolean => {
        return hasSubscriptionWithTag(SUBSCRIPTIONS_TAGS.OPTIM_TURPE)
    })

    /**
     * Vérifie si l'utilisateur a accès au module Exploitant Chauffagiste (Energy Manager IoT)
     * @returns True si l'utilisateur a accès au module, false sinon
     */
    const getHasModuleExploitantChauffagiste = computed((): boolean => {
        return hasSubscriptionWithTag(SUBSCRIPTIONS_TAGS.CHAUFFAGE)
    })

    /**
     * Vérifie si l'utilisateur a accès au dashboard Optimisation Contractuelle
     * @returns True si l'utilisateur a accès au dashboard, false sinon
     */
    const getHasModuleOptimTurpeDashboard = computed((): boolean => {
        return hasSubscriptionWithTag(SUBSCRIPTIONS_TAGS.OPTIM_TURPE_DASHBOARD)
    })

    /**
     * Vérifie si l'utilisateur a l'offre IRVE (tag irve sur une souscription active)
     * @returns True si l'utilisateur a l'offre IRVE, false sinon
     */
    const getHasOfferIrve = computed((): boolean => {
        return hasSubscriptionWithTag(SUBSCRIPTIONS_TAGS.IRVE)
    })

    /**
     * Vérifie si l'utilisateur a l'offre PV (tag pv sur une souscription active)
     * @returns True si l'utilisateur a l'offre PV, false sinon
     */
    const getHasOfferPV = computed((): boolean => {
        return hasSubscriptionWithTag(SUBSCRIPTIONS_TAGS.PV)
    })

    /**
     * Vérifie si l'utilisateur a l'offre Courtage (tag comparateur d'offres sur une souscription active)
     * @returns True si l'utilisateur a l'offre Courtage, false sinon
     */
    const getHasOfferCourtage = computed((): boolean => {
        return hasSubscriptionWithTag(SUBSCRIPTIONS_TAGS.COMPARATEUR_OFFRE)
    })

    /**
     * Module « Analyse factures » (DEV-711) — gate via tag Stripe
     * `analyse-facture`. Donne accès à l'espace « Mes factures » (dépôt + vue,
     * régime invoice-only). La visibilité par compteur reste autoritative côté
     * api-metier (la détection d'anomalie n'est jamais exposée).
     */
    const getHasModuleAnalyseFacture = computed((): boolean => {
        return hasSubscriptionWithTag(SUBSCRIPTIONS_TAGS.ANALYSE_FACTURE)
    })

    // ================================
    // FONCTIONNALITÉS BETA
    // ================================

    /**
     * Récupère les fonctionnalités beta activées pour l'entreprise de l'utilisateur
     * @returns Liste des beta features ou undefined
     */
    const getBetaFeatures = computed(() => {
        return companyBetaFeatures.value
    })

    // ================================
    // ENTREPRISES MULTIPLES
    // ================================

    /**
     * Indique si l'utilisateur a accès à plusieurs entreprises
     * @returns True si l'utilisateur a plusieurs entreprises, false sinon
     */
    const getHasMultipleCompanies = computed((): boolean => {
        return multipleCompanies.value.length > 0
    })

    /**
     * Récupère la liste des entreprises de l'utilisateur
     * @returns Liste des entreprises
     */
    const getMultipleCompanies = computed(() => {
        return multipleCompanies.value
    })

    /**
     * Indique si l'utilisateur a au moins une *autre* entreprise (≠ celle courante)
     * cliente, c'est-à-dire avec une offre socle vivante (DEV-2252).
     * Sert à proposer le bouton "Changer d'espace entreprise" sur PageSubscriptionCancelled
     * pour que l'user ne reste pas bloqué sur une company cancelled.
     */
    const getHasOtherActiveCompany = computed((): boolean => {
        return multipleCompanies.value.some((c) => {
            if (c._id === companyId.value) return false
            return hasLiveSocleSubscription(c.operation?.stripe?.subscriptions)
        })
    })

    // ================================
    // MISE À JOUR INFORMATIONS UTILISATEUR
    // ================================

    /**
     * Met à jour les informations utilisateur dans le store
     * @param userInfo - Informations utilisateur à mettre à jour
     */
    function updateUserInfo(userInfo: {
        firstname?: string
        lastname?: string
        phone?: string | null
        img?: string | null
        alarme?: { notifications: any }
    }) {
        if (userInfo.firstname !== undefined)
            firstname.value = userInfo.firstname
        if (userInfo.lastname !== undefined) lastname.value = userInfo.lastname
        if (userInfo.phone !== undefined) phone.value = userInfo.phone
        if (userInfo.img !== undefined) img.value = userInfo.img
        if (userInfo.alarme !== undefined) alarme.value = userInfo.alarme
    }

    // ================================
    // DÉCONNEXION
    // ================================

    /**
     * Réinitialise le store à son état initial (état + suppression du token en localStorage).
     * À utiliser pour un reset explicite du store ; pour la déconnexion utilisateur, préférer logout().
     */
    function $reset() {
        token.value = ""
        previousRoute.value = ""
        userFromEmbed.value = false
        adminHomeys.value = false
        sessionId.value = ""
        userId.value = ""
        firstname.value = ""
        lastname.value = ""
        phone.value = null
        alarme.value = null
        username.value = ""
        img.value = null
        companyId.value = ""
        companyName.value = ""
        companyIdcompany.value = undefined
        companyLegalForm.value = undefined
        companyAddress.value = undefined
        companyType.value = undefined
        companyRepresentant.value = undefined
        companyCreatedAt.value = undefined
        companyPhone.value = undefined
        companyImg.value = undefined
        companyBetaFeatures.value = {}
        companyStripeSubscriptions.value = []
        companySourcesCount.value = 0
        companyPlafond.value = null
        multipleCompanies.value = []
        role.value = null
        subscription.value = { plan: "", active: false }
        clearTokenFromStorage()
    }

    /**
     * Déconnecte l'utilisateur : réinitialise tous les stores applicatifs sauf le store config (référentiels / scope),
     * puis le token et l'état auth (via reset global).
     */
    function logout() {
        const rootStore = useRootStore()
        rootStore.resetAllStores(["config"])
    }

    // ================================
    // HYDRATATION DEPUIS LE STORAGE
    // ================================

    /**
     * Restaure la session depuis le localStorage (token persistant, clé homeys_auth_token).
     * Si un token valide et non expiré est trouvé, il est mis dans le store sans réécrire le storage.
     * @returns true si une session valide a été restaurée, false sinon
     */
    function hydrateFromStorage(): boolean {
        if (typeof window === "undefined") return false
        try {
            const storedToken = window.localStorage.getItem(AUTH_TOKEN_KEY)
            if (!storedToken?.trim()) return false
            try {
                const decoded = jwtDecode(storedToken) as { exp: number }
                if (decoded.exp * 1000 <= Date.now()) {
                    clearTokenFromStorage()
                    return false
                }
            } catch {
                clearTokenFromStorage()
                return false
            }
            token.value = storedToken

            // Tracking V0 : restaurer adminHomeys + sessionId depuis localStorage.
            // Si absent (ex. première migration, autre tab), fallback false / nouvelle session.
            try {
                const storedAdmin =
                    window.localStorage.getItem(ADMIN_HOMEYS_KEY)
                adminHomeys.value = storedAdmin === "true"
            } catch {
                adminHomeys.value = false
            }
            try {
                const storedSession =
                    window.localStorage.getItem(SESSION_ID_KEY)
                if (storedSession?.trim()) {
                    sessionId.value = storedSession
                } else {
                    // Pas de session_id → on en génère un (cas legacy : utilisateur avec
                    // un JWT valide mais migré sur la nouvelle version pour la 1ère fois).
                    generateNewSession()
                }
            } catch {
                generateNewSession()
            }

            return true
        } catch {
            return false
        }
    }

    // ================================
    // EXPORT STORE
    // ================================

    return {
        // État
        token,
        adminHomeys,
        sessionId,
        userId,
        firstname,
        lastname,
        phone,
        username,
        img,
        companyId,
        companyName,
        companyIdcompany,
        companyLegalForm,
        companyAddress,
        companyType,
        companyRepresentant,
        companyCreatedAt,
        companyPhone,
        companyImg,
        companyBetaFeatures,
        companyStripeSubscriptions,
        companySourcesCount,
        companyPlafond,
        setCompanyPlafond,
        multipleCompanies,
        role,
        previousRoute,
        subscription,
        alarme,
        // Computed
        isLogged: getIsLogged,
        isTokenValid: getIsTokenValid,
        isClientHomeys: getIsClientHomeys,
        isSubscriptionActive: getIsSubscriptionActive,
        subscriptionPlan: getSubscriptionPlan,
        isEmployeWriteOrHigher: getIsEmployeWriteOrHigher,
        isUc2Pro: getIsUc2Pro,
        isUc2Particulier: getIsUc2Particulier,
        isClientCancelled: getIsClientCancelled,
        hasUnpaidSubscriptions: getHasUnpaidSubscriptions,
        daysUntilDataDeletion: getDaysUntilDataDeletion,
        isCompanyAdmin: getIsCompanyAdmin,
        isCompanyOwner: getIsCompanyOwner,
        hasOfferEnergyManager: getHasOfferEnergyManager,
        getCurrentOffer,
        hasModuleOptimTurpe: getHasModuleOptimTurpe,
        hasModuleOptimTurpeDashboard: getHasModuleOptimTurpeDashboard,
        hasModuleExploitantChauffagiste: getHasModuleExploitantChauffagiste,
        hasOfferIrve: getHasOfferIrve,
        hasOfferPV: getHasOfferPV,
        hasOfferCourtage: getHasOfferCourtage,
        hasModuleAnalyseFacture: getHasModuleAnalyseFacture,
        getBetaFeatures,
        hasMultipleCompanies: getHasMultipleCompanies,
        getMultipleCompanies,
        hasOtherActiveCompany: getHasOtherActiveCompany,

        // Setters
        setToken,
        setHeaderAuthorization,
        setUserFromEmbed,
        setAdminHomeys,
        generateNewSession,

        // Reset
        $reset,

        // Actions asynchrones
        fetchUser,
        updateUserInfo,
        logout,
        hydrateFromStorage,

        // États de chargement
        isFetchingUser,
        // États d'erreur
        hasErrorUser,
        errorUser,

        // États de succès
        isSuccessUser,
    }
})
